import router from './router'
import { useAppStoreWithOut } from '@/store/modules/app'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { generateAdminRoleRoutesFromServer } from '@/utils/setupAdminRouterAccess'
import { firstAccessibleMenuPath } from '@/utils/adminRouteFilter'
import { clearAdminSessionAfterAccessDenied } from '@/utils/revokeAdminSession'
import { ElMessage } from 'element-plus'
import { i18n } from '@/plugins/vueI18n'

const { start, done } = useNProgress()

const { loadStart, loadDone } = usePageLoading()

router.beforeEach(async (to, from, next) => {
  start()
  loadStart()
  const permissionStore = usePermissionStoreWithOut()
  const appStore = useAppStoreWithOut()
  const userStore = useUserStoreWithOut()
  if (userStore.getUserInfo) {
    if (to.path === '/login') {
      next({ path: '/' })
    } else {
      if (permissionStore.getIsAddRouters) {
        next()
        return
      }

      // 开发者可根据实际情况进行修改
      const roleRouters = userStore.getRoleRouters || []

      // 是否使用动态路由
      if (appStore.getDynamicRouter) {
        appStore.serverDynamicRouter
          ? await permissionStore.generateRoutes('server', roleRouters as AppCustomRouteRecordRaw[])
          : await permissionStore.generateRoutes('frontEnd', roleRouters as string[])
      } else {
        const { grants, userRoles } = await generateAdminRoleRoutesFromServer()
        const mainRoutes = permissionStore.addRouters.filter((r) => r.name !== '404Page')
        const first = firstAccessibleMenuPath(mainRoutes, userRoles, grants)
        if (!first) {
          ElMessage.error(i18n.global.t('login.noAdminMenuAccess'))
          await clearAdminSessionAfterAccessDenied()
          next({ path: '/login', replace: true })
          return
        }
        permissionStore.addRouters.forEach((route) => {
          router.addRoute(route as unknown as RouteRecordRaw)
        })
        let redirectPath = decodeURIComponent((from.query.redirect || to.path) as string)
        if (first && (redirectPath === '/' || redirectPath === '')) {
          redirectPath = first
        }
        let resolved = router.resolve(redirectPath)
        if (resolved.matched.length === 0 && first) {
          redirectPath = first
          resolved = router.resolve(redirectPath)
        }
        const nextData =
          to.path === redirectPath
            ? { ...to, replace: true }
            : { path: redirectPath, replace: true }
        permissionStore.setIsAddRouters(true)
        next(nextData)
        return
      }

      permissionStore.addRouters.forEach((route) => {
        router.addRoute(route as unknown as RouteRecordRaw) // 动态添加可访问路由表
      })
      const redirectPath = from.query.redirect || to.path
      const redirect = decodeURIComponent(redirectPath as string)
      const nextData = to.path === redirect ? { ...to, replace: true } : { path: redirect }
      permissionStore.setIsAddRouters(true)
      next(nextData)
    }
  } else {
    if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
      next()
    } else {
      next(`/login?redirect=${to.path}`) // 否则全部重定向到登录页
    }
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done() // 结束Progress
  loadDone()
})
